import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { SessionListDataSource } from '@go-modules/services/session-list-datasource/session-list.datasource';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { sessionToken, Session as SessionModel } from 'go-modules/models/session/session.service';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { BehaviorSubject, Subject, filter, takeUntil } from 'rxjs';
import { MessageDialogComponent } from '@go-modules/components/dialogs/message-dialog/message-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { TaskType } from '@go-modules/interfaces/task';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';

export enum ZeroStates {
	BATCH_CREATE_SESSION = 1,
	NO_VIDEOS = 2,
	START_ACTIVITY = 3
}

export enum LearnFromFeedbackZeroState {
	NOT_SUBMITTED = 'NOT_SUBMITTED',
	NO_FEEDBACK_YET = 'NO_FEEDBACK_YET',
	FEEDBACK_HELD = 'FEEDBACK_HELD'
}

@Component({
	selector: 'session-zero-state',
	templateUrl: './session-zero-state.component.html',
	styleUrls: ['./session-zero-state.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SessionZeroStateComponent implements OnInit, OnDestroy {
	@Input() public taskType: TaskType;
	@Output() public startActivity = new EventEmitter();
	@Output() public viewInstructions = new EventEmitter();
	@Output() public inviteLearners = new EventEmitter<void>();
	@Input() public noVideosTitleKey = 'session-zero-state_no-videos';
	@Input() public noVideosSubtextKey = 'session-zero-state_no-videos-instructor-subtext';
	@Input() public noVideosSubtextParams: Record<string, unknown> = {};
	@Input() public showFilteredZeroState = true;
	@Input() public showInviteButton = false;
	@Input() public showCreateVideoButton = false;
	@Input() public showLearnerZeroState = false;
	@Input() public learnFromFeedbackState: LearnFromFeedbackZeroState | null = null;
	public zeroStates = ZeroStates;
	public learnFromFeedbackZeroStates = LearnFromFeedbackZeroState;
	public learnFromFeedbackContent = {
		[LearnFromFeedbackZeroState.NOT_SUBMITTED]: {
			title: 'session-zero-state_no-submitted-videos-title',
			subtext: 'session-zero-state_no-submitted-videos-subtext'
		},
		[LearnFromFeedbackZeroState.NO_FEEDBACK_YET]: {
			title: 'session-zero-state_no-feedback-title',
			subtext: 'session-zero-state_no-feedback-subtext'
		},
		[LearnFromFeedbackZeroState.FEEDBACK_HELD]: {
			title: 'session-zero-state_feedback-hold-title',
			subtext: 'session-zero-state_feedback-hold-subtext'
		}
	};
	public componentDestroyed$$ = new Subject();
	public canStartActivity$$ = new BehaviorSubject<boolean>(false);
	public getSessionCreationDisabledMessage$$ = new BehaviorSubject<string>('');
	public TaskType = TaskType;

	constructor (
		public sessionListDataSource: SessionListDataSource,
		private cdr: ChangeDetectorRef,
		private featureFlag: NgxFeatureFlagService,
		private ngxActivityService: NgxActivityService,
		private dialog: MatDialog,
		private translate: TranslateService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(sessionToken) private Session: ReturnType<typeof SessionModel>
	) {}

	public ngOnInit (): void {
		this.getLicenseAndValidate();
	}

	public ngOnDestroy (): void {
		this.componentDestroyed$$.next(true);
		this.componentDestroyed$$.complete();
		this.canStartActivity$$.complete();
		this.getSessionCreationDisabledMessage$$.complete();
	}

	public get isActivityTasksEnabled (): boolean {
		const activity = this.selectedService.getActivity();
		return activity.show_tasks || this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG');
	}

	public getTitleTranslationKey (): string {
		const activity = this.selectedService.getActivity();
		if (this.isActivityTasksEnabled && activity.isConversationBoard()) {
			return 'task-title_present-conversation-board';
		}
		if (this.isActivityTasksEnabled && this.ngxActivityService.shouldUseReviewVideoAction(activity)) {
			return 'task-title_present-comment-only';
		}
		if (this.isActivityTasksEnabled) {
			return 'activity-view_create-a-video';
		}
		return 'session-zero-state_start-activity';
	}

	public getSubtextTranslationKey (): string {
		const activity = this.selectedService.getActivity();
		if (this.isActivityTasksEnabled && activity.isConversationBoard()) {
			return 'session-zero-state_no-videos-presenter-subtext-conversation-board';
		}
		if (this.isActivityTasksEnabled && this.ngxActivityService.shouldUseReviewVideoAction(activity)) {
			return 'session-zero-state_no-videos-presenter-subtext-comment-only';
		}
		if (this.isActivityTasksEnabled) {
			return 'session-zero-state_no-videos-presenter-subtext-activity-task';
		}
		return 'session-zero-state_no-videos-presenter-subtext';
	}

	public getButtonTranslationKey (): string {
		const activity = this.selectedService.getActivity();
		if (this.isActivityTasksEnabled && activity.isConversationBoard()) {
			return 'activity-view_join-conversation';
		}
		if (this.isActivityTasksEnabled && this.ngxActivityService.shouldUseReviewVideoAction(activity)) {
			return 'activity-view_review-video';
		}
		if (this.isActivityTasksEnabled) {
			return 'activity-view_create-video';
		}
		return 'session-zero-state_start-activity';
	}

	public getZeroStateStatus () {
		if (this.showLearnerZeroState) {
			return ZeroStates.START_ACTIVITY;
		}

		if (this.hasReviewerRoleOrHigher()) {
			if (this.shouldDisplayCreateVideosForPresentersZeroState()) {
				return ZeroStates.BATCH_CREATE_SESSION;
			}
			return ZeroStates.NO_VIDEOS;
		}
		return ZeroStates.START_ACTIVITY;
	}

	public shouldDisplayCreateVideosForPresentersZeroState () {
		const activity = this.selectedService.getActivity();
		return this.hasInstructorRoleOrHigher() &&
			activity && activity.live_session_enabled &&
			(!activity.is_conversation || this.isGroupLiveReviewActivity(activity)) &&
			!activity.has_single_recording_attempt;
	}

	public getLiveReviewTitleTranslationKey (): string {
		return this.isGroupLiveReviewActivity(this.selectedService.getActivity())
			? 'session-zero-state_group-live-review-title'
			: 'session-zero-state_live-review-title';
	}

	public getActivityName () {
		return this.selectedService.getActivity().name;
	}

	public createBatchSessions () {
		if (this.canStartActivity$$.value === false) {
			return;
		}

		const activity = this.selectedService.getActivity();
		const session = this.Session.model({
			group_id: activity.group_id,
			activity_id: activity.activity_id,
			batch: true
		});
		session.save()
			.then(() => {
				this.sessionListDataSource.reload();
				this.cdr.detectChanges();
			})
			.catch((result) => {
				if (result.status === 422 && result.data.error === 'NoPaidPresentersException') {
					MessageDialogComponent.open(this.dialog, {
						data: {
							title: this.translate.instant('activity-view_no-paid-students-title'),
							message: this.translate.instant('activity-view_no-paid-students-message')
						}
					});
				}
			});
	}

	public startActivityIfAllowed (): void {
		if (this.canStartActivity$$.value === false) {
			return;
		}

		this.startActivity.emit();
	}

	private hasInstructorRoleOrHigher () {
		return this.selectedService.getGroup().hasInstructorRole(true);
	}

	private hasReviewerRoleOrHigher () {
		return this.selectedService.getGroup().hasReviewerRole(true);
	}

	private isGroupLiveReviewActivity (activity): boolean {
		return activity.live_session_enabled &&
			activity.is_conversation &&
			activity.has_response_media;
	}

	private validateCanStartActivity () {
		this.canStartActivity$$.next(this.ngxActivityService.canStartActivity());
		this.getSessionCreationDisabledMessage$$.next(this.ngxActivityService.getSessionCreationDisabledMessage());
	}

	private getLicenseAndValidate () {
		this.selectedService.selectedSubject
			.asObservable()
			.pipe(
				takeUntil(this.componentDestroyed$$),
				filter((selected: any) => selected.group)
			)
			.subscribe(() => {
				this.validateCanStartActivity();
			});
	}
}
